import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/shadcn-components/ui/card";
import { Input } from "@/shadcn-components/ui/input";
import { Button } from "@/shadcn-components/ui/button";
import { Plus, Minus } from "lucide-react";

interface TradeModifyContentProps {
  stopLoss: number | null;
  stopLossInput: string;
  takeProfit: number | null;
  takeProfitInput: string;
  onStopLossChange: (change: number) => void;
  onStopLossInputChange: (value: string) => void;
  onTakeProfitChange: (change: number) => void;
  onTakeProfitInputChange: (value: string) => void;
  onSaveChanges?: () => void;
  isTrading?: boolean;
  /** Price decimal digits of the symbol — drives step size and formatting. */
  digits?: number;
}

export default function TradeModifyContent({
  stopLoss,
  stopLossInput,
  takeProfit,
  takeProfitInput,
  onStopLossChange,
  onStopLossInputChange,
  onTakeProfitChange,
  onTakeProfitInputChange,
  onSaveChanges,
  isTrading = false,
  digits = 5,
}: TradeModifyContentProps) {
  // One tap moves the level by one point of the symbol (10^-digits).
  const step = Math.pow(10, -digits);
  return (
    <div className="space-y-4">
      {/* Stop Loss */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Stop Loss</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onStopLossChange(-step)}
              className="px-3"
            >
              <Minus className="h-3 w-3" />
            </Button>
            
            <Input
              type="text"
              value={stopLossInput}
              onChange={(e) => onStopLossInputChange(e.target.value)}
              placeholder="0.00000"
              className="flex-1 text-center font-mono"
            />
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => onStopLossChange(step)}
              className="px-3"
            >
              <Plus className="h-3 w-3" />
            </Button>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                onStopLossInputChange("0");
              }}
              className="flex-1 text-xs text-muted-foreground"
            >
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Take Profit */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Take Profit</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onTakeProfitChange(-step)}
              className="px-3"
            >
              <Minus className="h-3 w-3" />
            </Button>
            
            <Input
              type="text"
              value={takeProfitInput}
              onChange={(e) => onTakeProfitInputChange(e.target.value)}
              placeholder="0.00000"
              className="flex-1 text-center font-mono"
            />
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => onTakeProfitChange(step)}
              className="px-3"
            >
              <Plus className="h-3 w-3" />
            </Button>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                onTakeProfitInputChange("0");
              }}
              className="flex-1 text-xs text-muted-foreground"
            >
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Save Changes Button */}
      <Card>
        <CardContent className="pt-4">
          <Button 
            className="w-full" 
            size="lg"
            onClick={onSaveChanges}
            disabled={isTrading}
          >
            {isTrading ? "Saving..." : "Save Changes"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}